/**
 * COMUNICADOS EM TRÊS PASSOS (27/10/2026, decisões do Heitor: f1 g1 h1 i1 k1; backend do Bastião em
 * feat/comprovante-do-comunicado). Aqui moram as regras puras: o público, o teste obrigatório e as recusas do lote.
 */

/** k1: órgão público × privado, pelo `isPublicSector` do cadastro (36 públicos e 155 privados ativos em 27/10/2026). */
export type Esfera = 'todas' | 'publico' | 'privado';

export interface FiltroDoPublico { esfera: Esfera; cidades: string[]; produtos: string[] }

export const FILTRO_VAZIO: FiltroDoPublico = { esfera: 'todas', cidades: [], produtos: [] };

/** A cidade como a tela mostra e filtra: "Cascavel/PR". Sem cidade, null (todos os 191 ativos têm, mas é dado). */
export function cidadeDoCliente(c: { city?: string | null; state?: string | null }): string | null {
  const city = (c?.city || '').trim();
  if (!city) return null;
  return c.state ? `${city}/${String(c.state).trim()}` : city;
}

/**
 * k1, produto: os códigos dos itens das assinaturas ATIVAS de cada cliente. O comunicado da descontinuação de um produto
 * vai para quem o tem hoje, não para quem já teve.
 */
export function produtosPorCliente(contratos: Array<{ clientId?: number | null; status?: string | null; items?: any }>): Map<number, Set<string>> {
  const porCliente = new Map<number, Set<string>>();
  for (const c of contratos || []) {
    if (c?.status !== 'Ativo' || !c.clientId) continue;
    const itens = Array.isArray(c.items) ? c.items : [];
    for (const it of itens) {
      const codigo = String(it?.productCode || '').trim();
      if (!codigo) continue;
      if (!porCliente.has(c.clientId)) porCliente.set(c.clientId, new Set());
      porCliente.get(c.clientId)!.add(codigo);
    }
  }
  return porCliente;
}

/** O cliente passa no filtro de público? Cidade e produto: qualquer um dos escolhidos (lista vazia = não filtra). */
export function passaNoPublico(
  c: { id: number; isPublicSector?: boolean | null; city?: string | null; state?: string | null },
  f: FiltroDoPublico,
  produtos: Map<number, Set<string>>,
): boolean {
  if (f.esfera === 'publico' && !c.isPublicSector) return false;
  if (f.esfera === 'privado' && c.isPublicSector) return false;
  if (f.cidades.length) {
    const cid = cidadeDoCliente(c);
    if (!cid || !f.cidades.includes(cid)) return false;
  }
  if (f.produtos.length) {
    const tem = produtos.get(c.id);
    if (!tem || !f.produtos.some((p) => tem.has(p))) return false;
  }
  return true;
}

/** A linha do passo 3 que diz o público escolhido, em palavras. */
export function descricaoDoPublico(f: FiltroDoPublico, status: 'Ativo' | 'all'): string {
  const partes: string[] = [];
  if (f.esfera === 'publico') partes.push('órgão público');
  if (f.esfera === 'privado') partes.push('privado');
  if (f.cidades.length) partes.push(f.cidades.join(', '));
  if (f.produtos.length) partes.push(`produto ${f.produtos.join(', ')}`);
  partes.push(status === 'Ativo' ? 'ativos' : 'todos os status');
  return partes.join(' · ');
}

/**
 * h1: acima deste número de e-mails o lote só sai depois de um teste do MESMO conteúdo. O servidor manda o número na
 * resposta do teste (`limiteSemTeste`) e é ele quem trava; este é só o valor antes do primeiro teste da sessão.
 */
export const LIMITE_SEM_TESTE = 10;

export function precisaDeTeste(emails: number, limite: number = LIMITE_SEM_TESTE): boolean {
  return emails > limite;
}

/**
 * O conteúdo que o teste cobre: assunto e corpo JÁ preenchidos e o anexo. "Vale só para o conteúdo testado" é literal
 * (um espaço a mais invalida, como no servidor); a ordem dos anexos não conta, e aqui há um anexo só.
 */
export interface ConteudoDoEnvio { assunto: string; corpo: string; anexo: { filename: string; contentBase64: string } | null }

export function mesmoConteudo(a: ConteudoDoEnvio | null | undefined, b: ConteudoDoEnvio): boolean {
  if (!a) return false;
  return a.assunto === b.assunto && a.corpo === b.corpo
    && (a.anexo?.filename || '') === (b.anexo?.filename || '')
    && (a.anexo?.contentBase64 || '') === (b.anexo?.contentBase64 || '');
}

/**
 * O comprovante diz qual modelo foi usado e se o texto foi mexido. Preencher os campos [[...]] do modelo não é editar
 * (é para isso que eles existem); mudar o assunto ou o HTML é. Sem modelo, o servidor grava "editado" sozinho.
 */
export function templateFoiEditado(tpl: { subject?: string | null; htmlContent?: string | null } | null | undefined, assunto: string, html: string): boolean {
  if (!tpl) return true;
  return (tpl.subject || '') !== assunto || (tpl.htmlContent || '') !== html;
}

/** Os códigos das recusas do lote ligadas ao teste (backend #545): decide-se pelo código, nunca pela palavra da frase. */
export const RECUSAS_DO_TESTE = ['falta_teste', 'teste_desconhecido', 'conteudo_mudou'] as const;

/**
 * As três recusas do lote ligadas ao teste (400 do send-batch, com `codigo` e `limiteSemTeste` no corpo). Todas pedem
 * "testar de novo", e a do reinício do servidor (`teste_desconhecido`) não pode parecer falha genérica: o teste vive em
 * memória, e um deploy entre o teste e o envio o apaga. O limite vem do servidor junto, para a tela não ter o número
 * escrito à mão.
 */
export function recusaDoLote(e: any): { texto: string; testarDeNovo: boolean; limiteSemTeste: number | null } {
  const status = e?.response?.status;
  const corpo = e?.response?.data || {};
  const msg = String(corpo.message || e?.message || '');
  const limite = Number(corpo.limiteSemTeste) > 0 ? Number(corpo.limiteSemTeste) : null;
  if (status === 400) {
    return { texto: msg, testarDeNovo: (RECUSAS_DO_TESTE as readonly string[]).includes(corpo.codigo), limiteSemTeste: limite };
  }
  return { texto: `Erro ao enviar: ${msg || 'erro desconhecido'}`, testarDeNovo: false, limiteSemTeste: null };
}
